import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
  QBox,
  QButton,
  QHeading,
  QSpacer,
  QStack,
  QSpinner,
  QHorizontalMetadata,
  QStringMetadataItem,
  QDateMetadataItem,
  useCurrentUser,
  QMenuButton,
  QMenuItem,
} from '@qualio/ui-components';
import { useArchiveTemplate, useCreateNewDraftTemplate, useRestoreTemplate } from '../../QualityEventTemplates/hooks';
import { useEventTemplateDetails } from '../../../../hooks';
import { EventTemplateDetailsResponse } from '../../../../types';
import EventTemplateStatusTag from './EventTemplateStatusTag';
import * as DisplayStrings from '../../../../displayStrings';
import { useNavigate } from 'react-router-dom';
import { EventTemplatePermissions, eventTemplateHeaderActions } from '../../../../utils/eventTemplatePermissionUtils';
import ExportEventTemplateButton from './Export/ExportEventTemplateButton/ExportEventTemplateButton';
import { ExportEventTemplateModal } from './Export/ExportEventTemplateModal/ExportEventTemplateModal';

type EventTemplateHeaderProps = {
  templateId: number;
  isMakeEffectiveBtnDisabled: boolean;
  isSavingTemplate: boolean;
  makeEffectiveBtnAction: () => void;
  saveAndExitBtnAction: () => void;
  deleteDraftBtnAction: () => void;
  changeOwnerBtnAction: () => void;
};

const EventTemplateHeader = ({
  templateId,
  isMakeEffectiveBtnDisabled,
  isSavingTemplate,
  makeEffectiveBtnAction,
  saveAndExitBtnAction,
  deleteDraftBtnAction,
  changeOwnerBtnAction,
}: EventTemplateHeaderProps) => {
  const { t } = useTranslation();
  const { companyId, userId, permissions, userGroups } = useCurrentUser();
  const role = userGroups[0];
  const { data: template, isLoading: isEventTemplateLoading } = useEventTemplateDetails(companyId, templateId);
  const { mutate: createNewDraftTemplate } = useCreateNewDraftTemplate(companyId);
  const { mutate: archiveTemplate } = useArchiveTemplate(companyId);
  const { mutate: restoreTemplate } = useRestoreTemplate(companyId);
  const navigate = useNavigate();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const handleOpenExportModal = () => {
    setIsExportModalOpen(true);
  };
  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
  };

  const templateData = template as EventTemplateDetailsResponse;

  const showMakeEffectiveSaveAndExitBtn = useMemo(
    () => templateData && templateData.status === 'draft' && templateData.owner_id === userId,
    [templateData, userId],
  );

  if (isEventTemplateLoading) {
    return <QSpinner />;
  }

  const { canDeleteDraftOrChangeOwner } = EventTemplatePermissions(
    templateData.status,
    templateData.owner_id,
    userId,
    role,
    Boolean(permissions['can_manage_quality_event_workflow']),
  );
  const { showChangeOwner, showDeleteDraft, showArchive, showRestore, hasVisibleActions } = eventTemplateHeaderActions(
    templateData.status,
    templateData.active,
    canDeleteDraftOrChangeOwner,
  );
  return (
    <>
      <QBox>
        <QButton variant="link" onClick={() => navigate('/templates')} leftIcon="ArrowLeft">
          {DisplayStrings.Back}
        </QButton>
      </QBox>
      <QStack direction="row" mb={1}>
        <QBox>
          <QHeading data-cy="event-template-name" size="lg">
            {templateData.name}
          </QHeading>
        </QBox>
        <QSpacer />
        <ExportEventTemplateButton onClick={handleOpenExportModal} />
        <ExportEventTemplateModal isOpen={isExportModalOpen} onClose={handleCloseExportModal} template={templateData} />
        {templateData.status === 'effective' && templateData.active && (
          <QButton
            data-cy="create-template-draft"
            variant="outline"
            onClick={() => createNewDraftTemplate(templateData.lineage_id!)}
          >
            {DisplayStrings.CreateTemplateDraft}
          </QButton>
        )}
        {showMakeEffectiveSaveAndExitBtn && (
          <>
            <QButton
              data-cy={'save-and-exit'}
              isDisabled={isSavingTemplate}
              isLoading={isSavingTemplate}
              variant="outline"
              onClick={saveAndExitBtnAction}
            >
              {DisplayStrings.SaveAndExit}
            </QButton>
            <QButton
              data-cy={'make-effective'}
              isDisabled={isMakeEffectiveBtnDisabled}
              onClick={makeEffectiveBtnAction}
            >
              {DisplayStrings.MakeEffective}
            </QButton>
          </>
        )}
        {hasVisibleActions && (
          <QMenuButton
            buttonLabel="event-template-kebab-menu"
            data-cy="event-template-kebab-menu"
            iconName="MoreVertical"
            itemSize="sm"
            variant="icon"
          >
            {showChangeOwner && (
              <QMenuItem data-cy="change-owner" onClick={changeOwnerBtnAction} aria-label="change-owner">
                {DisplayStrings.ChangeTemplateOwner}
              </QMenuItem>
            )}
            {showDeleteDraft && (
              <QMenuItem
                data-cy="delete-draft-template"
                onClick={deleteDraftBtnAction}
                aria-label="delete-draft-template"
              >
                {DisplayStrings.DeleteDraft}
              </QMenuItem>
            )}
            {showArchive && (
              <QMenuItem
                data-cy="archive-template"
                onClick={() => archiveTemplate(String(templateData.id))}
                aria-label="archive-template"
              >
                Archive
              </QMenuItem>
            )}
            {showRestore && (
              <QMenuItem
                data-cy="restore-template"
                onClick={() => restoreTemplate(String(templateData.id))}
                aria-label="restore-template"
              >
                Restore
              </QMenuItem>
            )}
          </QMenuButton>
        )}
      </QStack>
      <QHorizontalMetadata>
        <EventTemplateStatusTag status={templateData.active ? templateData.status : 'archived'} />
        <QStringMetadataItem
          label={t('common.labels.version')}
          value={`${templateData.major_version}.${templateData.minor_version}`}
        />
        <QStringMetadataItem
          data-cy="template-owner"
          label={t('common.labels.templateOwner')}
          value={templateData.owner ?? '--'}
        />
        <QDateMetadataItem
          label={t('common.labels.lastModified')}
          value={templateData.updated_at ? DateTime.fromSeconds(templateData.updated_at).toJSDate() : undefined}
          data-cy="event-template-last-modified"
          withTime
        />
      </QHorizontalMetadata>
    </>
  );
};

export default EventTemplateHeader;
